import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useUserContext } from '../contexts/user-context'
import { useStoreContext } from '../contexts/store-context'
import { get } from '../utils/http'
import { WebSocketInvocation } from './webSocketInvocation'
import { useAlertContext } from '../contexts/alert-context'

export default function useSignalRFeed<TFeedObject>(
  signalRUrl: string,
  initialDataBaseUrl: string,
  invocations: Array<WebSocketInvocation<TFeedObject>>) {
  const { storeState } = useStoreContext()
  const { userState } = useUserContext()
  const { alert } = useAlertContext()

  const [initialFeed, setInitialFeed] = useState<Array<TFeedObject>>([])
  const [loading, setLoading] = useState(true)

  const connectionRef = useRef<HubConnection | null>(null)

  const fetchInitialFeedData = useCallback(async (storeId: string) => {
    try {
      const data = await get<Array<TFeedObject>>(`${initialDataBaseUrl}/${storeId}`)
      setInitialFeed(data)
    } catch (error) {
      console.log('Could not fetch initial feed data', error)
    } finally {
      setLoading(false)
    }
  }, [initialDataBaseUrl])

  useEffect(() => {
    let currentConnection: HubConnection | null = null
    let started = false

    const setupSignalR = async (storeId: string) => {
      const connection = new HubConnectionBuilder()
        .withUrl(signalRUrl, {
          accessTokenFactory: () => userState.user.token ?? '',
          withCredentials: false,
        })
        .withAutomaticReconnect()
        .build()

      currentConnection = connection
      connectionRef.current = connection

      invocations.forEach((invocation) => {
        connection.on(invocation.name, invocation.action)
      })

      connection.onreconnecting(() => {
        setLoading(true)
      })

      connection.onreconnected(async () => {
        if (connection.state === HubConnectionState.Connected && storeId) {
          await connection.invoke('JoinStore', storeId)
        }
        setLoading(false)
      })

      try {
        await connection.start()
        started = true
        if (connection.state === HubConnectionState.Connected) {
          await connection.invoke('JoinStore', storeId)
        }
        setLoading(false)
      } catch (error) {
        console.log('Could not setup SignalRFeed', error)
        alert.error('Någonting gick fel. Försök igen genom att ladda om sidan.', 7)
        setLoading(false)
      }
    }

    async function setup() {
      const storeId = storeState.selectedStore?.id
      if (!storeId) return

      setLoading(true)

      await fetchInitialFeedData(storeId)
      await setupSignalR(storeId)
    }

    setup()

    return () => {
      if (currentConnection) {
        invocations.forEach((invocation) => {
          currentConnection!.off(invocation.name, invocation.action)
        })
        if (started) currentConnection.stop()
      }
    }

  }, [storeState.selectedStore?.id, signalRUrl, fetchInitialFeedData, alert, userState.user.token, invocations])

  useEffect(() => {
    const onPageHide = () => {
      if (connectionRef.current) {
        connectionRef.current.stop()
      }
    }

    window.addEventListener('pagehide', onPageHide, false)

    return () => {
      window.removeEventListener('pagehide', onPageHide, false)
    }
  }, [])

  return { initialFeed, loading }
}